import { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { SlotGrid } from "@/components/SlotGrid";
import { PayMethodPicker, EtransferBox } from "@/components/EtransferPay";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Clock, Video, Phone, MapPin, Ticket, Loader2, CreditCard, ArrowLeft } from "lucide-react";

const addMinutes = (hhmm, mins) => {
  const [h, m] = hhmm.split(":").map(Number);
  const t = h * 60 + m + mins;
  return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const MEETINGS = [["Video", Video], ["Phone", Phone], ["In Person", MapPin]];
const offeredModes = (svc) => (svc?.modes_offered?.length ? MEETINGS.filter(([m]) => svc.modes_offered.includes(m)) : MEETINGS);
const priceFor = (svc, mode) => {
  const v = svc?.mode_prices?.[mode];
  return v === null || v === undefined || v === "" ? Number(svc?.price || 0) : Number(v);
};
const money = (n, cur) => (n > 0 ? `$${Number.isInteger(n) ? n : n.toFixed(2)} ${cur}` : "Free");
const tzName = (tz) => (tz || "America/Toronto").split("/").pop().replace(/_/g, " ");
// Office wall-clock time → the visitor's own local time (only shown when their zone differs).
const localHint = (dateStr, hhmm, tz = "America/Toronto") => {
  const mine = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!dateStr || !hhmm || mine === tz) return "";
  try {
    const [y, mo, d] = dateStr.split("-").map(Number); const [h, mi] = hhmm.split(":").map(Number);
    const guess = Date.UTC(y, mo - 1, d, h, mi);
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(guess)).map((x) => [x.type, x.value]));
    const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
    const at = new Date(guess - (wall - guess));
    return ` (${at.toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })} your time)`;
  } catch { return ""; }
};

// Closed or fully booked day: point to the next open time, or offer an emergency appointment.
function NoTimes({ date, duration, closed, emergencySvc, contact, onPick, onEmergency }) {
  const [next, setNext] = useState(undefined);
  useEffect(() => {
    let stale = false;
    setNext(undefined);
    api.get(`/public/appointments/next-available?date=${date}&duration=${duration}`).then((r) => { if (!stale) setNext(r.data); }).catch(() => { if (!stale) setNext(null); });
    return () => { stale = true; };
  }, [date, duration]);
  const label = next?.date ? `${new Date(`${next.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })} at ${to12h(next.start)}` : "";
  return (
    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="pub-closed-note">
      <div className="text-sm text-amber-800">{closed ? "We're closed on this day." : "No times left on this day."}</div>
      {next === undefined ? <div className="text-xs text-slate-400">Finding the next available time…</div> : next?.date ? (
        <button type="button" data-testid="pub-next-available" onClick={() => onPick(next.date, next.start)} className="w-full text-left rounded-lg bg-white border border-primary/30 px-3 py-2 hover:bg-primary/5">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Next available</div>
          <div className="text-sm font-semibold text-primary">{label}</div>
        </button>
      ) : <div className="text-xs text-slate-500">No open times in the next 60 days — please contact our office.</div>}
      {emergencySvc && (
        <button type="button" data-testid="pub-book-emergency" onClick={onEmergency} className="text-xs font-semibold text-rose-700 hover:underline">Need help sooner? Book an emergency appointment →</button>
      )}
      {!emergencySvc && (contact?.phone || contact?.whatsapp) && (
        <div className="text-xs text-slate-600" data-testid="pub-urgent-contact">For urgent help before then, call or WhatsApp us: <b>{contact.phone || contact.whatsapp}</b></div>
      )}
    </div>
  );
}

const to12h = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

// Defined outside the page so it is not re-created (and the form remounted) on every key press.
function Shell({ config, children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4 flex items-center justify-between">
        <Brand />
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:block">Book an Appointment</span>
      </header>
      <main className="flex-1 w-full max-w-2xl mx-auto p-4 sm:p-8">{children}</main>
      <footer className="text-center text-[11px] text-slate-400 py-4 space-y-1" data-testid="public-booking-footer">
        <div>© {new Date().getFullYear()} {config?.company || "Chahal Overseas Immigration"}{config?.contact?.rcic ? ` · ${config.contact.rcic}` : ""}</div>
        {(config?.contact?.phone || config?.contact?.email || config?.contact?.address) && (
          <div data-testid="public-booking-contact" className="text-slate-400">
            {[config?.contact?.address, config?.contact?.phone, config?.contact?.email].filter(Boolean).join(" · ")}
          </div>
        )}
      </footer>
    </div>
  );
}

function PriceBreakdown({ svc, mode, total, hasCode, currency }) {
  return (
    <div className="text-sm divide-y divide-slate-100" data-testid="pub-price-breakdown">
      <div className="flex justify-between py-1.5"><span className="text-slate-500">Appointment</span><span className="font-medium text-slate-800 text-right">{svc.name}</span></div>
      <div className="flex justify-between py-1.5"><span className="text-slate-500">{mode}</span><span className="font-medium text-slate-800" data-testid="pub-breakdown-mode-price">{money(priceFor(svc, mode), currency)}</span></div>
      {hasCode && <div className="flex justify-between py-1.5 text-emerald-600"><span>Free booking code</span><span data-testid="pub-breakdown-code">−{money(priceFor(svc, mode), currency)}</span></div>}
      <div className="flex justify-between pt-2 font-bold text-slate-900"><span>Total</span><span data-testid="pub-breakdown-total">{hasCode ? "Free" : money(total, currency)}</span></div>
    </div>
  );
}

export default function PublicBooking() {
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null); // chosen service (catalog item) or null = show menu
  const [showAll, setShowAll] = useState(false);
  const [avail, setAvail] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", phone: "", type: "", type_id: "", date: todayStr(),
    start_time: "", end_time: "", meeting_type: "Video", notes: "", is_emergency: false, code: "",
  });

  useEffect(() => {
    api.get("/public/booking-config").then((r) => setConfig(r.data)).catch(() => setConfig(false));
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Payment canceled — you can try again.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    setVerifying(true);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid" && data.appointment) {
          setResult({ appointment: data.appointment, contact_name: data.contact_name, paid: true });
          setVerifying(false);
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          toast.error("Payment was not completed.");
          setVerifying(false);
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
      } catch (_) { /* keep polling */ }
      if (tries < 20) setTimeout(poll, 2000);
      else { setVerifying(false); toast.error("Still confirming your payment — please check back shortly."); }
    };
    poll();
  }, []);

  const fee = config?.booking_fee || { enabled: true, amount: 50, currency: "CAD" };

  // Service menu — prefer the rich catalog; fall back to plain appointment types for legacy setups.
  const catalog = useMemo(() => {
    const c = (config?.appointment_catalog || []).filter((x) => x.active !== false);
    if (c.length) return c;
    return (config?.appointment_types || []).map((t, i) => ({
      id: `type-${i}`, name: t, price: Number(fee.amount || 0),
      duration: Number(config?.appointment_duration || 30), description: "", meeting_mode: "Video",
    }));
  }, [config, fee.amount]);

  const total = useMemo(() => priceFor(selected, f.meeting_type), [selected, f.meeting_type]);
  const [codeOk, setCodeOk] = useState(null); // null = not checked yet, true / false = validated
  const codeText = f.code.trim();
  useEffect(() => {
    setCodeOk(null);
    if (!codeText) return;
    const t = setTimeout(() => api.post("/public/booking-codes/validate", { code: codeText }).then((r) => setCodeOk(!!r.data.valid)).catch(() => setCodeOk(false)), 400);
    return () => clearTimeout(t);
  }, [codeText]);
  const hasCode = codeOk === true;
  const needsPay = !hasCode && total > 0;
  const [payMethod, setPayMethod] = useState(null);
  const method = needsPay ? (fee.enabled ? payMethod || "card" : "etransfer") : null;
  const [receipt, setReceipt] = useState(null);
  const payRequired = method === "card";

  // Load availability sized to the selected service's duration.
  useEffect(() => {
    if (!selected || !f.date) return;
    let stale = false;
    setLoadingAvail(true);
    api.get(`/public/appointments/availability?date=${f.date}&duration=${selected.duration || 30}&emergency=${/emergency|urgent/i.test(selected.name || "")}`)
      .then((r) => { if (!stale) setAvail(r.data); }).catch(() => { if (!stale) setAvail(null); }).finally(() => { if (!stale) setLoadingAvail(false); });
    return () => { stale = true; };
  }, [f.date, selected]);

  const dur = () => selected?.duration || avail?.duration || 30;
  const pickSlot = (s) => setF((p) => ({ ...p, start_time: s, end_time: addMinutes(s, dur()) }));

  const chooseService = (svc) => {
    const modes = offeredModes(svc).map(([m]) => m);
    const mode = modes.includes(svc.meeting_mode) ? svc.meeting_mode : modes[0];
    setSelected(svc);
    setF((p) => ({ ...p, type: svc.name, type_id: svc.id, meeting_type: mode, start_time: "", end_time: "" }));
    setAvail(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const backToMenu = () => { setSelected(null); setF((p) => ({ ...p, start_time: "", end_time: "", notes: "", code: "" })); };

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Please enter your name"); return; }
    if (!f.email.trim() && !f.phone.trim()) { toast.error("Please add your email or phone so we can reach you"); return; }
    if (!f.start_time) { toast.error("Please pick a time"); return; }
    if (method === "etransfer" && !receipt) { toast.error("Please upload your e-Transfer receipt or screenshot to book"); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (hasCode) {
        const { data } = await api.post("/public/appointments/book", { ...payload, code: f.code.trim() });
        setResult(data);
      } else if (method === "etransfer") {
        const fd = new FormData();
        fd.append("data", JSON.stringify({ ...payload, code: "" }));
        fd.append("file", receipt);
        const { data } = await api.post("/public/appointments/etransfer", fd);
        toast.dismiss();
        setResult(data);
        setReceipt(null);
      } else if (method === "card") {
        const { data } = await api.post("/public/appointments/checkout", { ...payload, origin_url: window.location.origin });
        if (data.free) setResult(data);
        else { window.location.href = data.checkout_url; return; }
      } else {
        const { data } = await api.post("/public/appointments/book", payload);
        setResult(data);
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const inputCls = "mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";
  const slots = (avail?.slots || []).filter((s) => s.available !== false);
  const isEmg = /emergency|urgent/i.test(selected?.name || "");
  const emergencySvc = config?.emergency?.enabled === false ? null : catalog.find((c) => /emergency|urgent/i.test(c.name || ""));
  useEffect(() => {
    if (f._jump && avail && !avail.closed && (avail.slots || []).some((s) => s.start === f._jump && s.available !== false)) {
      setF((p) => ({ ...p, start_time: p._jump, end_time: addMinutes(p._jump, dur()), _jump: undefined }));
    }
  }, [avail]); // eslint-disable-line
  const priceLabel = (svc) => {
    const ps = offeredModes(svc).map(([m]) => priceFor(svc, m));
    const lo = Math.min(...ps);
    return lo !== Math.max(...ps) ? `From ${money(lo, fee.currency)}` : money(lo, fee.currency);
  };

  if (verifying) return (
    <Shell config={config}>
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="public-verifying">
        <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
        <h2 className="font-head font-bold text-xl text-slate-900">Confirming your payment…</h2>
        <p className="text-slate-500 text-sm mt-1">This only takes a moment. Please don't close this window.</p>
      </div>
    </Shell>
  );

  if (config === false) return <Shell config={config}><div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">Booking is temporarily unavailable. Please try again later.</div></Shell>;
  if (config === null) return <Shell config={config}><div className="p-10 text-center"><Loader2 className="h-7 w-7 text-primary animate-spin mx-auto" /></div></Shell>;

  if (result) {
    const ap = result.appointment;
    return (
      <Shell config={config}>
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="public-booking-confirmation">
          <div className="h-14 w-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
          <h2 className="font-head font-extrabold text-2xl text-slate-900">You're booked!</h2>
          <p className="text-slate-500 mt-2 text-sm">Thanks {result.contact_name}. We've scheduled your appointment and our team will be in touch to confirm the details.</p>
          <div className="mt-5 text-left bg-slate-50 rounded-xl p-4 text-sm space-y-1 max-w-sm mx-auto">
            <div><b>Type:</b> {ap.type}</div>
            <div><b>Date:</b> {ap.date} at {ap.start_time}{ap.end_time ? `–${ap.end_time}` : ""}</div>
            <div><b>Meeting:</b> {ap.meeting_type}</div>
            {result.paid && <div className="text-emerald-600 font-semibold pt-1">Payment received ✓</div>}
            {!result.paid && result.free && <div className="text-slate-500 pt-1">Booked with a free code ✓</div>}
            {result.etransfer && <div className="text-emerald-600 font-semibold pt-1" data-testid="pub-etransfer-done">e-Transfer receipt received ✓ — we'll confirm once the money arrives.</div>}
          </div>
          <button data-testid="public-book-another" onClick={() => { setResult(null); backToMenu(); setF((p) => ({ ...p, name: "", email: "", phone: "" })); }} className="mt-6 bg-primary text-white px-6 py-2.5 rounded-xl font-semibold text-sm">Book another appointment</button>
        </div>
      </Shell>
    );
  }

  // STEP 1 — service menu
  if (!selected) {
    const visible = showAll ? catalog : catalog.slice(0, 4);
    return (
      <Shell config={config}>
        <div className="mb-5">
          <h1 className="font-head font-extrabold text-3xl text-slate-900">Book an Appointment</h1>
          <p className="text-slate-500 text-sm mt-1">Select the appointment that fits your needs, then pick a time.</p>
        </div>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3"><CalendarCheck className="h-4 w-4 text-primary" /> Select Appointment</div>
        {catalog.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">No appointment types are available right now.</div>
        ) : (
          <div className="space-y-3" data-testid="public-service-list">
            {visible.map((svc) => (
              <div key={svc.id} data-testid={`service-card-${svc.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start justify-between gap-4 transition-shadow hover:shadow-md">
                <div className="min-w-0">
                  <h3 className="font-head font-bold text-slate-900">{svc.name}</h3>
                  <div className="text-sm text-primary font-semibold mt-0.5">{priceLabel(svc)} · {svc.duration} min</div>
                  {svc.description && <p className="text-sm text-slate-500 mt-2 whitespace-pre-line leading-relaxed">{svc.description}</p>}
                </div>
                <button data-testid={`service-select-${svc.id}`} onClick={() => chooseService(svc)} className="shrink-0 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-primary/90">Select</button>
              </div>
            ))}
          </div>
        )}
        {catalog.length > 4 && (
          <button data-testid="service-show-all" onClick={() => setShowAll((v) => !v)} className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-primary">
            {showAll ? "Show fewer appointments" : "Show all appointments"}
          </button>
        )}
      </Shell>
    );
  }

  // STEP 2 — details + time for the selected service
  return (
    <Shell config={config}>
      <button data-testid="service-back" onClick={backToMenu} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-primary mb-3"><ArrowLeft className="h-4 w-4" /> Change appointment type</button>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4" data-testid="selected-service">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-head font-extrabold text-xl text-slate-900">{selected.name}</h2>
            <div className="text-sm text-primary font-semibold mt-0.5">{priceLabel(selected)} · {selected.duration} min</div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-slate-400 bg-slate-50 px-3 py-1.5 rounded-lg"><Clock className="h-3.5 w-3.5" /> {selected.duration} min</span>
        </div>
        {selected.description && <p className="text-sm text-slate-500 mt-3 whitespace-pre-line leading-relaxed">{selected.description}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5" data-testid="public-book-form">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs font-medium text-slate-500">Full name *</label><input data-testid="pub-name" value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} className={inputCls} /></div>
          <div><label className="text-xs font-medium text-slate-500">Email</label><input data-testid="pub-email" value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} className={inputCls} /></div>
          <div><label className="text-xs font-medium text-slate-500">Phone</label><input data-testid="pub-phone" value={f.phone} onChange={(e) => setF((p) => ({ ...p, phone: e.target.value }))} className={inputCls} /></div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">How would you like to meet?</label>
          <div className={`grid gap-2 mt-1 ${offeredModes(selected).length === 3 ? "grid-cols-3" : offeredModes(selected).length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
            {offeredModes(selected).map(([m, Icon]) => (
              <button key={m} type="button" data-testid={`pub-meeting-${m.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setF((p) => ({ ...p, meeting_type: m }))}
                className={`flex flex-col items-center justify-center px-3 py-2 rounded-xl border text-sm font-semibold transition-colors ${f.meeting_type === m ? "bg-primary text-white border-primary" : "bg-white border-slate-200 text-slate-600 hover:border-primary/40"}`}>
                <span className="inline-flex items-center gap-1.5"><Icon className="h-4 w-4" /> {m}</span>
                {fee.enabled && <span className={`text-[11px] font-medium ${f.meeting_type === m ? "text-white/80" : "text-slate-400"}`} data-testid={`pub-meeting-price-${m.replace(/\s+/g, "-").toLowerCase()}`}>{money(priceFor(selected, m), fee.currency)}</span>}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">Preferred date</label>
          <input data-testid="pub-date" type="date" min={todayStr()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value, start_time: "", end_time: "" })} className={inputCls} />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-500 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Pick a time</label>
            {f.start_time && <span className="text-xs font-semibold text-primary" data-testid="pub-selected-slot">Selected: {f.start_time}{localHint(f.date, f.start_time, config?.timezone)}</span>}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5" data-testid="pub-tz-note">Times are in {tzName(config?.timezone)} time.{config?.min_notice_hours > 0 && !/emergency|urgent/i.test(selected?.name || "") ? ` Please book at least ${config.min_notice_hours} hours ahead — for anything sooner, choose an emergency appointment.` : ""}</div>
          {loadingAvail ? <div className="text-xs text-slate-400 mt-2">Loading times…</div> : (avail?.closed || slots.length === 0) ? (
            <NoTimes date={f.date} duration={dur()} closed={avail?.closed} emergencySvc={isEmg ? null : emergencySvc} contact={config?.contact}
              onPick={(d, s) => setF((p) => ({ ...p, date: d, start_time: "", end_time: "", _jump: s }))} onEmergency={() => chooseService(emergencySvc)} />
          ) : (
            <div className="mt-2" data-testid="pub-slots">
              <SlotGrid slots={slots} value={f.start_time} onPick={pickSlot} duration={dur()} testid="pub-slot" />
            </div>
          )}
        </div>

        <div><label className="text-xs font-medium text-slate-500">Anything we should know? (optional)</label><textarea data-testid="pub-notes" rows={3} value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} className={inputCls} /></div>

        {total > 0 && (
          <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="pub-payment-box">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-primary" /> Price breakdown</div>
            <PriceBreakdown svc={selected} mode={f.meeting_type} total={total} hasCode={hasCode} currency={fee.currency} />
            {needsPay && <PayMethodPicker cardEnabled={fee.enabled} method={method} setMethod={setPayMethod} />}
            {method === "etransfer" && <EtransferBox email={fee.etransfer_email} amountLabel={money(total, fee.currency)} receipt={receipt} setReceipt={setReceipt} />}
            <div>
              <label className="text-xs font-medium text-slate-500 flex items-center gap-1"><Ticket className="h-3.5 w-3.5" /> Have a code?</label>
              <input data-testid="pub-code" value={f.code} onChange={(e) => setF((p) => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="Enter one-time code" className={inputCls} />
              {hasCode && <div className="text-[11px] text-emerald-600 mt-1" data-testid="pub-code-valid">Code accepted — your appointment is free.</div>}
              {codeText && codeOk === null && <div className="text-[11px] text-slate-400 mt-1">Checking code…</div>}
              {codeText && codeOk === false && <div className="text-[11px] text-rose-600 mt-1" data-testid="pub-code-invalid">This code is invalid or has already been used.</div>}
            </div>
          </div>
        )}

        <button data-testid="pub-submit" onClick={submit} disabled={saving || (method === "etransfer" && !receipt)} className="w-full bg-primary text-white px-5 py-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <CalendarCheck className="h-4 w-4" /> {saving ? "Please wait…" : payRequired ? `Pay ${money(total, fee.currency)} & book` : method === "etransfer" ? (receipt ? "Upload receipt & book" : "Upload your receipt to book") : "Request appointment"}
        </button>
        <p className="text-[11px] text-center text-slate-400">By booking, you agree to be contacted by {config?.company || "our office"} about your appointment.{payRequired ? " Payments are processed securely by Stripe." : ""}</p>
      </div>
    </Shell>
  );
}
